import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

/**
 * useFormPageConfig — wraps the Task-6 (5c-1) page-config CRUD API mounted
 * under `/api/job-types/:id/forms/:formId`:
 *   GET/POST  /page-types         (POST { name, ordinal })
 *   PATCH/DELETE /page-types/:pageTypeId
 *   GET/POST  /scenarios          (POST { name, pageTypeIds: string[] })
 *   PATCH/DELETE /scenarios/:scenarioId
 * plus `patchForm` for the two form-level fields (allowsAttachments,
 * pageIdentityStrategy) on `PATCH /forms/:formId`.
 *
 * Pattern mirrors `useJobTypeForms.ts`: useState/useEffect/useCallback,
 * the shared `api.*` wrapper, list/create/patch/delete shape (mutations
 * call `load()` internally, so no separate refetch is exported).
 *
 * The hook is a no-op (empty lists, no fetch) until both jobTypeId and
 * formId are non-empty — the Pages tab passes formId only once a form is
 * selected.
 */

export type PageIdentityStrategy = "none" | "positional" | "fingerprint" | "manual";

export interface FormPageType {
  id: string;
  jobTypeFormId: string;
  name: string;
  ordinal: number;
  /** R-252 — when true, this page type marks the start of a new document
   * when splitStrategy is "on_occurrence". */
  isDocumentStart?: boolean;
}

export interface FormCompletenessScenario {
  id: string;
  jobTypeFormId: string;
  name: string;
  pageTypeIds: string[];
}

export function useFormPageConfig(jobTypeId: string, formId: string) {
  const [pageTypes, setPageTypes] = useState<FormPageType[]>([]);
  const [scenarios, setScenarios] = useState<FormCompletenessScenario[]>([]);
  const [loading, setLoading] = useState(true);

  const base = jobTypeId && formId ? `/job-types/${jobTypeId}/forms/${formId}` : "";

  const load = useCallback(async () => {
    if (!base) {
      setPageTypes([]);
      setScenarios([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [pts, scs] = await Promise.all([
        api.get<FormPageType[]>(`${base}/page-types`),
        api.get<FormCompletenessScenario[]>(`${base}/scenarios`),
      ]);
      setPageTypes(Array.isArray(pts) ? pts : []);
      setScenarios(Array.isArray(scs) ? scs : []);
    } catch {
      // Non-blocking: render empty panels if the fetch fails.
      setPageTypes([]);
      setScenarios([]);
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => {
    void load();
  }, [load]);

  // ─── Page types ────────────────────────────────────────────────────────────

  const createPageType = useCallback(
    async (args: { name: string; ordinal: number }): Promise<FormPageType> => {
      const created = await api.post<FormPageType>(`${base}/page-types`, args);
      await load();
      return created;
    },
    [base, load],
  );

  const updatePageType = useCallback(
    async (
      pageTypeId: string,
      args: { name?: string; ordinal?: number; isDocumentStart?: boolean },
    ): Promise<FormPageType> => {
      const updated = await api.patch<FormPageType>(`${base}/page-types/${pageTypeId}`, args);
      await load();
      return updated;
    },
    [base, load],
  );

  const deletePageType = useCallback(
    async (pageTypeId: string): Promise<void> => {
      // DELETE returns 204, or 409 { error: "page_type_in_use" } — callers
      // catch the ApiError and surface the message as a toast.
      await api.delete(`${base}/page-types/${pageTypeId}`);
      await load();
    },
    [base, load],
  );

  // ─── Scenarios ─────────────────────────────────────────────────────────────

  const createScenario = useCallback(
    async (args: { name: string; pageTypeIds: string[] }): Promise<FormCompletenessScenario> => {
      const created = await api.post<FormCompletenessScenario>(`${base}/scenarios`, args);
      await load();
      return created;
    },
    [base, load],
  );

  const deleteScenario = useCallback(
    async (scenarioId: string): Promise<void> => {
      await api.delete(`${base}/scenarios/${scenarioId}`);
      await load();
    },
    [base, load],
  );

  // ─── Form-level fields ───────────────────────────────────────────────────────

  const patchForm = useCallback(
    async (args: {
      allowsAttachments?: boolean;
      pageIdentityStrategy?: PageIdentityStrategy;
      enforcePageOrder?: boolean;
      autoReorderPages?: boolean;
      incompleteHandling?: "park" | "continue"; // E-053
    }): Promise<void> => {
      if (!jobTypeId || !formId) return;
      await api.patch(`/job-types/${jobTypeId}/forms/${formId}`, args);
    },
    [jobTypeId, formId],
  );

  return {
    pageTypes,
    scenarios,
    loading,
    createPageType,
    updatePageType,
    deletePageType,
    createScenario,
    deleteScenario,
    patchForm,
  };
}
